import { type FC, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  TextInput,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import { Modal } from "../../../../components/modal/Modal";
import {
  AvailablePlan,
  CompanySubscriptionDetailsEntry,
  FullCompanyApiType,
  SubscriptionFeeSchedule,
} from "../../../../util/types";
import {
  PaymentsInterimBillingInterval,
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
  SubscriptionLevelEnum,
  SubscriptionOptionEnum,
} from "@shared_packages/toolkit/types";
import TransactionFeesScheduleEditor from "./TransactionFeesScheduleEditor";
import BillingProviderStep, {
  billingProviderLabel,
  isAdminBillingProvider,
  type AdminBillingProvider,
} from "./BillingProviderStep";
import type { SubscriptionPricingFxRates } from "./TransactionFeesScheduleEditor";
import {
  formatFeeScheduleSummary,
  formatMoneyFromPence,
  preventNumberInputWheel,
  pricingModeToPaymentsPricingMode,
} from "./subscription-billing.helpers";
import {
  buildCurrentSnapshot,
  getDefaultTrialDays,
  getFlow,
  initialFeeSchedule,
  INTERIM_INTERVAL_OPTIONS,
  PRICING_MODE_LABELS,
  resolveCurrentLevel,
  resolveCurrentType,
  resolvePricingModeFromBilling,
  STEP_LABELS,
  SUBSCRIPTION_WIZARD_PRICING_MODE_OPTIONS,
  TIER_LABELS,
  TIERS,
} from "./subscription-wizard.helpers";
import {
  buildPaymentsWizardDraft,
  isConfigurablePaymentsWizardMode,
  preparePaymentsWizardDraftForSave,
  supportsInterimBillingForPaymentsDraft,
  validatePaymentsWizardStep,
} from "./subscription-wizard-payments-draft";
import type { PaymentsWizardCapabilities } from "./subscription-wizard-payments-draft";
import {
  useNotification,
  NotificationTypes,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";

export { getDefaultTrialDays } from "./subscription-wizard.helpers";

interface SubscriptionWizardModalProps {
  data: FullCompanyApiType;
  /** When set, locks the wizard to one product line and skips the type step. */
  lockedType?: SubscriptionOptionEnum;
  activeSub?: CompanySubscriptionDetailsEntry;
  onClose: () => void;
  onSuccess: () => void;
}

type AdminInitialSubscriptionStatus = "incomplete" | "trialing" | "active";

function sanitizeMoneyInput(raw: string): string {
  const normalized = raw.replace(",", ".");
  const match = normalized.match(/^\d*(?:\.\d{0,2})?/);
  return match?.[0] ?? "";
}

function moneyInputToMinor(raw: string): number | null {
  if (!raw || raw === ".") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────

const WizardContainer = styled.div`
  width: 800px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    max-height: none;
    padding: 16px;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #495b6c;
  line-height: 1;
  padding: 0;
  &:hover {
    color: #13273f;
  }
`;

const StepBarOuter = styled.div`
  position: relative;
  margin: 0 -24px;
  padding: 0 24px;
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 24px;
    z-index: 1;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, #fff 30%, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, #fff 30%, transparent);
  }
`;

const StepBarScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 0;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StepItem = styled.div<{ $active: boolean; $done: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
  font-size: 11px;
  color: ${({ $active, $done }) =>
    $active ? "#027aff" : $done ? "#2cd19e" : "#bbb"};
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.2s;
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $active, $done }) =>
    $done ? "#2cd19e" : $active ? "#027aff" : "#e8e8e8"};
  color: ${({ $active, $done }) => ($done || $active ? "#fff" : "#bbb")};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
`;

const StepConnector = styled.div<{ $done: boolean }>`
  width: 20px;
  height: 2px;
  background: ${({ $done }) => ($done ? "#2cd19e" : "#e8e8e8")};
  margin: 0 4px;
  flex-shrink: 0;
  transition: background 0.2s;
`;

const OptionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionCard = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  padding: 12px 14px;
  border-radius: 8px;
  border: 2px solid ${({ $selected }) => ($selected ? "#027aff" : "#e0e0e0")};
  background: ${({ $selected }) => ($selected ? "#f0f7ff" : "#fafafa")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: border-color 0.15s, background 0.15s;
  &:hover {
    border-color: ${({ $selected, $disabled }) =>
      $disabled ? "#e0e0e0" : $selected ? "#027aff" : "#9bc8ff"};
  }
`;

const OptionCardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #13273f;
  margin-bottom: 2px;
`;

const OptionCardDesc = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ConfigLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #13273f;
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
`;

// ── Current state banner ───────────────────────────────────────────────────

const CurrentStateBanner = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
`;

const CurrentStateTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999;
  margin-bottom: 8px;
`;

const CurrentStateGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
`;

const CurrentStateChip = styled.div`
  font-size: 12px;
  color: #495b6c;
  display: flex;
  gap: 4px;
`;

const ChipLabel = styled.span`
  color: #aaa;
`;

const ChipValue = styled.span`
  font-weight: 600;
  color: #13273f;
  white-space: pre-wrap;
`;

// ── Diff review ─────────────────────────────────────────────────────────────

const DiffTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const DiffHeader = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 24px 1fr;
  gap: 0;
  background: #f4f5f6;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
`;

const DiffRow = styled.div<{ $changed?: boolean }>`
  display: grid;
  grid-template-columns: 140px 1fr 24px 1fr;
  gap: 0;
  padding: 7px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f0f0f0;
  background: ${({ $changed }) => ($changed ? "#fffbe6" : "transparent")};
  &:last-child {
    border-bottom: none;
  }
`;

const DiffLabel = styled.div`
  color: #666;
  font-weight: 500;
`;

const DiffOld = styled.div<{ $changed?: boolean }>`
  color: ${({ $changed }) => ($changed ? "#c00" : "#495b6c")};
  text-decoration: ${({ $changed }) => ($changed ? "line-through" : "none")};
  opacity: ${({ $changed }) => ($changed ? 0.7 : 1)};
  white-space: pre-wrap;
  word-break: break-word;
`;

const DiffArrow = styled.div`
  color: #ccc;
  text-align: center;
  font-weight: 700;
`;

const DiffNew = styled.div<{ $changed?: boolean }>`
  color: ${({ $changed }) => ($changed ? "#2a7" : "#495b6c")};
  font-weight: ${({ $changed }) => ($changed ? 600 : 400)};
  white-space: pre-wrap;
  word-break: break-word;
`;

const InfoBanner = styled.div<{ $variant?: "warning" | "info" }>`
  background: ${({ $variant }) =>
    $variant === "warning" ? "#fff8ec" : "#f0f7ff"};
  border: 1px solid
    ${({ $variant }) => ($variant === "warning" ? "#f5c878" : "#9bc8ff")};
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #495b6c;
  line-height: 1.5;
`;

const OptionsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #13273f;
  margin-bottom: 4px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #13273f;
  cursor: pointer;
`;

const PricingVisibleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const InlineInput = styled.input`
  width: 80px;
  padding: 6px 8px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 13px;
  margin-top: 6px;
  &:focus {
    outline: none;
    border-color: #027aff;
  }
`;

const IntervalToggle = styled.div`
  display: flex;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  margin-bottom: 12px;
`;

const IntervalBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${({ $active }) => ($active ? "#027aff" : "#666")};
  box-shadow: ${({ $active }) =>
    $active ? "0 1px 3px rgba(0,0,0,.1)" : "none"};
  transition: all 0.15s;
`;

const TierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 8px;
`;

const TierCard = styled.div<{ $selected: boolean }>`
  padding: 14px;
  border-radius: 8px;
  border: 2px solid ${({ $selected }) => ($selected ? "#027aff" : "#e0e0e0")};
  background: ${({ $selected }) => ($selected ? "#f0f7ff" : "#fafafa")};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s;
  &:hover {
    border-color: ${({ $selected }) => ($selected ? "#027aff" : "#9bc8ff")};
  }
`;

const TierName = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #13273f;
`;

const TierPrice = styled.div`
  font-size: 13px;
  color: #027aff;
  font-weight: 600;
  margin-top: 4px;
`;

const TierLimit = styled.div`
  font-size: 11px;
  color: #888;
  margin-top: 4px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const SubscriptionWizardModal: FC<SubscriptionWizardModalProps> = ({
  data,
  lockedType,
  activeSub,
  onClose,
  onSuccess,
}) => {
  const billingEntry = activeSub;
  const subscriptionId = billingEntry?.subscriptionId ?? null;

  const [subscriptionType, setSubscriptionType] =
    useState<SubscriptionOptionEnum>(
      lockedType ?? billingEntry?.subscriptionOption ?? resolveCurrentType(data)
    );
  const isUkPaymentsOnly =
    subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY;
  const isEuPaymentsOnly =
    subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY_EU;
  const isPaymentsSubscription = isUkPaymentsOnly || isEuPaymentsOnly;
  const paymentsProduct = isEuPaymentsOnly ? "eu" : "uk";
  const [selectedLevel, setSelectedLevel] = useState<SubscriptionLevelEnum>(
    billingEntry?.subscriptionLevel ?? resolveCurrentLevel(data)
  );
  const [billingInterval, setBillingInterval] = useState<"month" | "year">(
    String(
      (billingEntry as any)?.subscriptionInterval ?? "month"
    ).toLowerCase() === "year"
      ? "year"
      : "month"
  );
  // "" is the sentinel for "let the user decide" — preserved as `null` on
  // the wire/DB, available for every subscription type. A brand-new draft
  // still defaults to GBP; an existing draft whose currency was left
  // undecided reopens as "".
  const [subscriptionCurrency, setSubscriptionCurrency] = useState(
    billingEntry ? billingEntry.currency ?? "" : "gbp"
  );
  const [initialStatus, setInitialStatus] =
    useState<AdminInitialSubscriptionStatus>(
      billingEntry
        ? ((billingEntry.status ?? "active") as AdminInitialSubscriptionStatus)
        : "incomplete"
    );
  const [preconfigurePreview, setPreconfigurePreview] = useState<any>(null);

  // ── Payments Only draft ───────────────────────────────────────────────
  const paymentsBillingCurrency = subscriptionCurrency;
  const [paymentsDraft, setPaymentsDraft] = useState(() =>
    buildPaymentsWizardDraft({
      product: paymentsProduct,
      mode: resolvePricingModeFromBilling(billingEntry, data),
      monthlyFeeMinor: billingEntry?.monthlyFeeMinor ?? null,
      fees: initialFeeSchedule(billingEntry, data),
      enabledPaymentCurrencies: isEuPaymentsOnly
        ? billingEntry?.enabledPaymentCurrencies ?? []
        : ["gbp"],
      capabilities: {
        canAccessInternationalOb: isEuPaymentsOnly,
        canAccessRecurringPaymentsUk:
          isUkPaymentsOnly && data.canAccessRecurringPaymentsUk === true,
        canAccessVrpUk: isUkPaymentsOnly && data.canAccessVrpUk === true,
        canAccessBulkPaymentsUk:
          isUkPaymentsOnly && data.canAccessBulkPaymentsUk === true,
      },
      interimInterval:
        isPaymentsSubscription &&
        (billingEntry?.subscriptionOption ===
          SubscriptionOptionEnum.PAYMENTS_ONLY ||
          billingEntry?.subscriptionOption ===
            SubscriptionOptionEnum.PAYMENTS_ONLY_EU)
          ? billingEntry.interimBillingInterval ?? null
          : isUkPaymentsOnly && !billingEntry
          ? data.paymentsInterimBillingInterval ?? null
          : null,
      billingCurrency: paymentsBillingCurrency,
    })
  );
  const ruleFeeScheduleRef = useRef(paymentsDraft.fees);
  const {
    mode: selectedMode,
    monthlyFeeMinor,
    fees: feeSchedule,
    enabledPaymentCurrencies,
    capabilities: {
      canAccessInternationalOb,
      canAccessRecurringPaymentsUk,
      canAccessVrpUk,
      canAccessBulkPaymentsUk,
    },
    interimInterval,
  } = paymentsDraft;

  // ── Status, trial & discount state ────────────────────────────────────
  const [trialMode, setTrialMode] = useState<"default" | "custom" | "none">(
    (billingEntry
      ? billingEntry.trialPeriodMode ?? "default"
      : data.trialPeriodMode ?? "default") as "default" | "custom" | "none"
  );
  const [trialDays, setTrialDays] = useState(
    billingEntry
      ? billingEntry.trialPeriodDays
        ? String(billingEntry.trialPeriodDays)
        : ""
      : data.trialPeriodDays
      ? String(data.trialPeriodDays)
      : ""
  );
  const effectiveDiscount = billingEntry?.subscriptionDiscount;
  const [hasDiscount, setHasDiscount] = useState(
    billingEntry
      ? (effectiveDiscount?.percentOff ?? 0) > 0
      : (data.discountPercentOff ?? 0) > 0
  );
  const [discountPercent, setDiscountPercent] = useState(
    billingEntry
      ? effectiveDiscount?.percentOff
        ? String(effectiveDiscount.percentOff)
        : ""
      : data.discountPercentOff
      ? String(data.discountPercentOff)
      : ""
  );
  const [discountDuration, setDiscountDuration] = useState<
    "once" | "forever" | "repeating"
  >(
    billingEntry
      ? effectiveDiscount?.duration ?? "once"
      : (data.discountDuration as "once" | "forever" | "repeating") ?? "once"
  );
  const [discountMonths, setDiscountMonths] = useState(
    billingEntry
      ? effectiveDiscount?.durationInMonths
        ? String(effectiveDiscount.durationInMonths)
        : ""
      : data.discountDurationInMonths
      ? String(data.discountDurationInMonths)
      : ""
  );
  const [paymentsOnlyPricingVisible, setPaymentsOnlyPricingVisible] =
    useState(true);
  const [monthlyFeeInput, setMonthlyFeeInput] = useState(
    billingEntry?.monthlyFeeMinor != null
      ? String(billingEntry.monthlyFeeMinor / 100)
      : ""
  );

  // ── Available plans from API ──────────────────────────────────────────
  const [plans, setPlans] = useState<AvailablePlan[]>([]);

  useEffect(() => {
    api
      .getAvailablePlans()
      .then(setPlans)
      .catch((e) =>
        console.error("[SubscriptionWizardModal] Failed to load plans", e)
      );
  }, []);

  const isBespokePaymentsOnly =
    isPaymentsSubscription && selectedMode === PaymentsPricingMode.BESPOKE;

  // The subscription's provider always follows the company's "Billing
  // provider permission" (the backend never takes it from this wizard's
  // payload), so the wizard edits that company setting in its own step and
  // applies it on save. Bespoke Payments is always Manual and has no step.
  const needsBillingProvider = !isBespokePaymentsOnly;
  const currentCompanyProvider = data.preferredBillingProvider ?? null;
  const [selectedProvider, setSelectedProvider] =
    useState<AdminBillingProvider | null>(
      isAdminBillingProvider(currentCompanyProvider)
        ? currentCompanyProvider
        : null
    );
  const billingProviderChanged =
    needsBillingProvider &&
    selectedProvider !== null &&
    selectedProvider !== currentCompanyProvider;

  // ── Wizard flow ───────────────────────────────────────────────────────
  const flow = getFlow(subscriptionType, lockedType, {
    includeBillingProvider: needsBillingProvider,
  });
  const [stepIdx, setStepIdx] = useState(0);
  const currentStep = flow[stepIdx];
  const [isSaving, setIsSaving] = useState(false);
  const { showNotification } = useNotification();
  const { authUser } = useAuthUserContext();
  const canManageCompanies = authUser.policies?.actionsOnCompanies === true;

  const hasActiveSubscription = Boolean(
    billingEntry &&
      !["deleted", "canceled", "cancelled", "deleted_in_process"].includes(
        (billingEntry.status ?? "").toLowerCase()
      )
  );

  const isTrialing = (billingEntry?.status ?? "").toLowerCase() === "trialing";

  const isCreateMode = !billingEntry;

  const isToolkit =
    subscriptionType === SubscriptionOptionEnum.TOOLKIT ||
    subscriptionType === SubscriptionOptionEnum.TOOLKIT_MOBILE;

  // Billing currency left for the customer to decide — the subscription
  // must stay incomplete until they actually pick one at activation.
  // Available for every subscription type, not just EU.
  const isCurrencyDeferred = subscriptionCurrency === "";
  const [pricingFxRates, setPricingFxRates] =
    useState<SubscriptionPricingFxRates | null>(null);
  const [pricingFxRatesLoading, setPricingFxRatesLoading] = useState(false);
  const [pricingFxRatesError, setPricingFxRatesError] = useState(false);

  // Toolkit/Toolkit Mobile tier prices, keyed by subscriptionLevel, for the
  // currently selected currency + interval. Sourced from the same
  // SubscriptionPriceProfile catalogue the backend actually resolves the
  // price from at creation time — not the legacy `plans` (GBP-only in
  // practice) catalogue used for tier limits below.
  const [tierPriceProfiles, setTierPriceProfiles] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (!isToolkit || currentStep !== "tier" || !subscriptionCurrency) {
      return;
    }
    let cancelled = false;
    api
      .getSubscriptionPriceCatalogue({
        subscriptionOption: subscriptionType,
        billingCurrency: subscriptionCurrency,
        interval: billingInterval,
      })
      .then((response) => {
        if (cancelled) return;
        const profiles = (response?.data ?? []) as Array<{
          subscriptionLevel?: string;
          fixedPriceMinorUnits?: number | null;
        }>;
        const byLevel: Record<string, number> = {};
        for (const profile of profiles) {
          if (
            profile.subscriptionLevel &&
            typeof profile.fixedPriceMinorUnits === "number"
          ) {
            byLevel[profile.subscriptionLevel] = profile.fixedPriceMinorUnits;
          }
        }
        setTierPriceProfiles(byLevel);
      })
      .catch(() => {
        if (!cancelled) setTierPriceProfiles({});
      });
    return () => {
      cancelled = true;
    };
  }, [
    isToolkit,
    subscriptionType,
    subscriptionCurrency,
    billingInterval,
    currentStep,
  ]);

  useEffect(() => {
    const shouldLoadRates =
      isEuPaymentsOnly &&
      selectedMode === PaymentsPricingMode.RULE_BASED &&
      currentStep === "configure_eu" &&
      enabledPaymentCurrencies.length > 0 &&
      // No fixed billing currency to convert to yet when the admin left it
      // for the customer to decide — nothing to fetch rates against.
      paymentsBillingCurrency !== "";

    if (!shouldLoadRates) {
      setPricingFxRates(null);
      setPricingFxRatesLoading(false);
      setPricingFxRatesError(false);
      return;
    }

    let cancelled = false;
    setPricingFxRatesLoading(true);
    setPricingFxRatesError(false);

    api
      .getSubscriptionPricingFxRates(paymentsBillingCurrency)
      .then((response) => {
        if (cancelled) return;
        const rates = response?.data ?? null;
        if (!rates?.ratesToBillingCurrency) {
          setPricingFxRates(null);
          setPricingFxRatesError(true);
          return;
        }
        setPricingFxRates(rates as SubscriptionPricingFxRates);
      })
      .catch(() => {
        if (cancelled) return;
        setPricingFxRates(null);
        setPricingFxRatesError(true);
      })
      .finally(() => {
        if (!cancelled) setPricingFxRatesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentStep,
    enabledPaymentCurrencies.length,
    isEuPaymentsOnly,
    paymentsBillingCurrency,
    selectedMode,
  ]);

  const canConfigureTrial =
    !isBespokePaymentsOnly &&
    initialStatus !== "active" &&
    (!hasActiveSubscription || isTrialing);

  // When type changes, reset step to 0 and switch flow
  const handleTypeChange = (t: SubscriptionOptionEnum) => {
    setSubscriptionType(t);
    setSubscriptionCurrency("gbp");
    if (
      t === SubscriptionOptionEnum.PAYMENTS_ONLY ||
      t === SubscriptionOptionEnum.PAYMENTS_ONLY_EU
    ) {
      const product =
        t === SubscriptionOptionEnum.PAYMENTS_ONLY_EU ? "eu" : "uk";
      setPaymentsDraft((current) =>
        buildPaymentsWizardDraft({
          ...current,
          product,
          enabledPaymentCurrencies: product === "eu" ? [] : ["gbp"],
          capabilities: {
            canAccessInternationalOb: product === "eu",
            canAccessRecurringPaymentsUk: false,
            canAccessVrpUk: false,
            canAccessBulkPaymentsUk: false,
          },
          interimInterval: null,
          billingCurrency: "gbp",
        })
      );
    }
    setStepIdx(0);
  };

  const setFeeSchedule = useCallback(
    (fees: SubscriptionFeeSchedule[]) => {
      ruleFeeScheduleRef.current = fees;
      setPaymentsDraft((current) =>
        buildPaymentsWizardDraft({
          ...current,
          fees,
          billingCurrency: paymentsBillingCurrency,
        })
      );
    },
    [paymentsBillingCurrency]
  );

  const handlePaymentsCapabilitiesChange = useCallback(
    (patch: Partial<PaymentsWizardCapabilities>) => {
      setPaymentsDraft((current) => {
        const next = buildPaymentsWizardDraft({
          ...current,
          capabilities: { ...current.capabilities, ...patch },
          billingCurrency: paymentsBillingCurrency,
        });
        if (next.mode === PaymentsPricingMode.RULE_BASED) {
          ruleFeeScheduleRef.current = next.fees;
        }
        return next;
      });
    },
    [paymentsBillingCurrency]
  );

  const handlePaymentsPricingModeChange = (mode: PaymentsPricingMode) => {
    setPaymentsDraft((current) => {
      if (current.mode === PaymentsPricingMode.RULE_BASED) {
        ruleFeeScheduleRef.current = current.fees;
      }
      return buildPaymentsWizardDraft({
        ...current,
        mode,
        monthlyFeeMinor:
          mode === PaymentsPricingMode.MONTHLY_FEE
            ? moneyInputToMinor(monthlyFeeInput)
            : current.monthlyFeeMinor,
        fees:
          mode === PaymentsPricingMode.RULE_BASED
            ? ruleFeeScheduleRef.current
            : current.fees,
        billingCurrency: paymentsBillingCurrency,
      });
    });
  };

  const handleEnabledPaymentCurrenciesChange = (currencies: string[]) => {
    setPaymentsDraft((current) => {
      const next = buildPaymentsWizardDraft({
        ...current,
        enabledPaymentCurrencies: currencies,
        billingCurrency: paymentsBillingCurrency,
      });
      if (next.mode === PaymentsPricingMode.RULE_BASED) {
        ruleFeeScheduleRef.current = next.fees;
      }
      return next;
    });
  };

  const handleMonthlyFeeChange = (value: number | null) => {
    setPaymentsDraft((current) =>
      buildPaymentsWizardDraft({
        ...current,
        monthlyFeeMinor: value,
        billingCurrency: paymentsBillingCurrency,
      })
    );
  };

  const handleSubscriptionCurrencyChange = (currency: string) => {
    setSubscriptionCurrency(currency);
    setPaymentsDraft((current) =>
      buildPaymentsWizardDraft({
        ...current,
        billingCurrency: currency,
      })
    );
  };

  const setInterimInterval = (
    interimInterval: PaymentsInterimBillingInterval | null
  ) => {
    setPaymentsDraft((current) =>
      buildPaymentsWizardDraft({
        ...current,
        interimInterval,
        billingCurrency: paymentsBillingCurrency,
      })
    );
  };

  const goNext = () => setStepIdx((i) => Math.min(i + 1, flow.length - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  // ── Helpers ───────────────────────────────────────────────────────────

  const getPlanPrice = (
    option: SubscriptionOptionEnum,
    level: SubscriptionLevelEnum,
    interval: "month" | "year"
  ): { formatted: string } => {
    // Only ever called for Toolkit/Toolkit Mobile (the "tier" step and its
    // review-step summary). Reads the currency-aware SubscriptionPriceProfile
    // catalogue fetched above — not the legacy GBP-only `plans` catalogue,
    // which is kept around only for `getPlanLimits` below.
    const amountMinor = tierPriceProfiles[level];
    if (typeof amountMinor !== "number") return { formatted: "—" };
    return {
      formatted: `${formatMoneyFromPence(
        amountMinor,
        subscriptionCurrency || "gbp"
      )}/${interval === "year" ? "yr" : "mo"}`,
    };
  };

  const getPlanLimits = (option: string, level: string): string => {
    const plan = plans.find(
      (p) => p.subscriptionOption === option && p.subscriptionLevel === level
    );
    if (!plan?.limits) return "";
    const l = plan.limits as Record<string, number | null>;
    const parts: string[] = [];
    if (l.usersLimit != null)
      parts.push(
        l.usersLimit === null ? "Unlimited users" : `${l.usersLimit} users`
      );
    return parts.join(" · ");
  };

  const interimSupported = supportsInterimBillingForPaymentsDraft(
    selectedMode,
    feeSchedule
  );

  useEffect(() => {
    if (!isBespokePaymentsOnly) return;
    if (initialStatus === "trialing") {
      setInitialStatus("incomplete");
    }
    if (trialMode !== "none") {
      setTrialMode("none");
      setTrialDays("");
    }
    if (hasDiscount) {
      setHasDiscount(false);
    }
  }, [hasDiscount, initialStatus, isBespokePaymentsOnly, trialMode]);

  useEffect(() => {
    if (!isCurrencyDeferred) return;
    if (initialStatus !== "incomplete") {
      setInitialStatus("incomplete");
    }
  }, [isCurrencyDeferred, initialStatus]);

  useEffect(() => {
    if (isBespokePaymentsOnly) return;
    if (initialStatus === "active" && trialMode !== "none") {
      setTrialMode("none");
      setTrialDays("");
    }
    if (initialStatus === "trialing" && trialMode === "none") {
      setTrialMode("default");
    }
  }, [initialStatus, isBespokePaymentsOnly, trialMode]);

  const statusStepValid =
    !isCreateMode ||
    initialStatus !== "trialing" ||
    trialMode === "default" ||
    (trialMode === "custom" && Number(trialDays) > 0);

  const buildPreconfigurePayload = () => {
    const preparedPaymentsDraft = preparePaymentsWizardDraftForSave(
      paymentsDraft,
      paymentsBillingCurrency
    );
    return {
      subscriptionOption: subscriptionType,
      subscriptionLevel: isPaymentsSubscription ? null : selectedLevel,
      subscriptionInterval: billingInterval.toUpperCase(),
      // "" is the "let the user decide" sentinel — send it as an explicit
      // `null`, distinct from a real currency choice.
      currency: subscriptionCurrency === "" ? null : subscriptionCurrency,
      initialStatus:
        isBespokePaymentsOnly && initialStatus === "trialing"
          ? "incomplete"
          : initialStatus,
      subscriptionConfirmed: isUkPaymentsOnly
        ? paymentsOnlyPricingVisible
        : undefined,
      paymentsPricingMode: isPaymentsSubscription
        ? pricingModeToPaymentsPricingMode(preparedPaymentsDraft.mode)
        : null,
      monthlyFeeMinor: isPaymentsSubscription
        ? preparedPaymentsDraft.monthlyFeeMinor
        : null,
      feeSchedules: isPaymentsSubscription
        ? preparedPaymentsDraft.feeSchedules
        : null,
      enabledPaymentCurrencies: isEuPaymentsOnly
        ? preparedPaymentsDraft.enabledPaymentCurrencies
        : undefined,
      interimBillingInterval: isPaymentsSubscription
        ? preparedPaymentsDraft.interimInterval
        : null,
      ...(isUkPaymentsOnly
        ? {
            // Never spread the whole capabilities draft here — it always
            // carries `canAccessInternationalOb` (EU-only), and the backend
            // rejects any non-EU patch that so much as mentions that key,
            // regardless of its value.
            canAccessRecurringPaymentsUk:
              preparedPaymentsDraft.capabilities.canAccessRecurringPaymentsUk,
            canAccessVrpUk: preparedPaymentsDraft.capabilities.canAccessVrpUk,
            canAccessBulkPaymentsUk:
              preparedPaymentsDraft.capabilities.canAccessBulkPaymentsUk,
          }
        : !isPaymentsSubscription
        ? {
            canAccessInternationalOb: data.canAccessInternationalOb === true,
            canAccessRecurringPaymentsUk:
              data.canAccessRecurringPaymentsUk === true,
            canAccessVrpUk: data.canAccessVrpUk === true,
            canAccessBulkPaymentsUk: data.canAccessBulkPaymentsUk === true,
          }
        : {}),
      trialPeriodMode:
        isBespokePaymentsOnly || initialStatus === "active"
          ? "none"
          : trialMode,
      trialPeriodDays:
        !isBespokePaymentsOnly &&
        initialStatus !== "active" &&
        trialMode === "custom"
          ? Number(trialDays)
          : null,
      subscriptionDiscount:
        !isBespokePaymentsOnly && hasDiscount
          ? {
              percentOff: Number(discountPercent || 0),
              duration: discountDuration,
              durationInMonths:
                discountDuration === "repeating"
                  ? Number(discountMonths || 0)
                  : null,
            }
          : null,
    };
  };

  useEffect(() => {
    if (!isCreateMode || initialStatus !== "active") {
      setPreconfigurePreview(null);
      return;
    }
    let cancelled = false;
    api
      .previewSubscriptionPreconfigure(data._id, buildPreconfigurePayload())
      .then((res) => {
        if (cancelled) return;
        setPreconfigurePreview(res?.data?.preview ?? res?.preview ?? null);
      })
      .catch(() => {
        if (!cancelled) setPreconfigurePreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    billingInterval,
    canAccessBulkPaymentsUk,
    canAccessInternationalOb,
    canAccessRecurringPaymentsUk,
    canAccessVrpUk,
    data._id,
    discountDuration,
    discountMonths,
    discountPercent,
    feeSchedule,
    enabledPaymentCurrencies,
    hasDiscount,
    initialStatus,
    interimInterval,
    isCreateMode,
    isToolkit,
    selectedLevel,
    selectedMode,
    monthlyFeeMinor,
    subscriptionCurrency,
    subscriptionType,
    trialDays,
    trialMode,
  ]);

  // ── Save ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (needsBillingProvider && !selectedProvider) {
      setStepIdx(flow.indexOf("billing_provider"));
      showNotification(
        "Choose the company's billing provider.",
        NotificationTypes.WARNING
      );
      return;
    }
    if (isPaymentsSubscription) {
      const validation = validatePaymentsWizardStep(
        paymentsDraft,
        isEuPaymentsOnly ? "eu" : "uk",
        paymentsBillingCurrency
      );
      const invalidStep = validation.valid
        ? null
        : {
            step: isEuPaymentsOnly
              ? ("configure_eu" as const)
              : ("configure_uk" as const),
            validation,
          };

      if (invalidStep) {
        const invalidStepIndex = flow.indexOf(invalidStep.step);
        if (invalidStepIndex >= 0) setStepIdx(invalidStepIndex);
        showNotification(
          invalidStep.validation.message ??
            "Complete the Payments Only pricing configuration.",
          NotificationTypes.WARNING
        );
        return;
      }
    }

    setIsSaving(true);
    try {
      if (billingProviderChanged) {
        // Applied first: the subscription save below validates against the
        // company's provider (and this also moves its other subscriptions).
        await api.post(`/company/${data._id}/set-billing-provider`, {
          preferredBillingProvider: selectedProvider,
        });
      }

      if (isCreateMode) {
        await api.createOrPreconfigureSubscription(
          data._id,
          buildPreconfigurePayload()
        );
        showNotification(
          "Subscription configuration saved",
          NotificationTypes.SUCCESS
        );
        onSuccess();
        onClose();
        return;
      }

      if (isToolkit) {
        await api.configureSubscription(
          data._id,
          subscriptionType,
          selectedLevel,
          billingInterval.toUpperCase()
        );
      } else if (isPaymentsSubscription) {
        const currentType = resolveCurrentType(data);
        const needsTypeSwitch =
          isUkPaymentsOnly &&
          hasActiveSubscription &&
          currentType !== SubscriptionOptionEnum.PAYMENTS_ONLY;
        const needsFreshCreate = isUkPaymentsOnly && !hasActiveSubscription;
        if (needsTypeSwitch || needsFreshCreate) {
          await api.configureSubscription(
            data._id,
            SubscriptionOptionEnum.PAYMENTS_ONLY,
            null,
            null
          );
        }

        const preparedPaymentsDraft = preparePaymentsWizardDraftForSave(
          paymentsDraft,
          paymentsBillingCurrency
        );

        await api.patchSubscriptionBillingConfig(data._id, subscriptionId, {
          paymentsPricingMode: pricingModeToPaymentsPricingMode(
            preparedPaymentsDraft.mode
          ),
          monthlyFeeMinor: preparedPaymentsDraft.monthlyFeeMinor,
          feeSchedules: preparedPaymentsDraft.feeSchedules,
          interimBillingInterval: preparedPaymentsDraft.interimInterval,
          ...(isEuPaymentsOnly
            ? {
                enabledPaymentCurrencies:
                  preparedPaymentsDraft.enabledPaymentCurrencies,
              }
            : {
                // Never spread the whole capabilities draft here — it
                // always carries `canAccessInternationalOb` (EU-only), and
                // the backend rejects any non-EU patch that so much as
                // mentions that key, regardless of its value.
                canAccessRecurringPaymentsUk:
                  preparedPaymentsDraft.capabilities
                    .canAccessRecurringPaymentsUk,
                canAccessVrpUk:
                  preparedPaymentsDraft.capabilities.canAccessVrpUk,
                canAccessBulkPaymentsUk:
                  preparedPaymentsDraft.capabilities.canAccessBulkPaymentsUk,
              }),
        });
      }

      if (isBespokePaymentsOnly && isTrialing) {
        await api.setTrialConfig(data._id, "none", null, subscriptionId);
      } else if (canConfigureTrial) {
        await api.setTrialConfig(
          data._id,
          trialMode,
          trialMode === "custom" ? Number(trialDays) : null,
          subscriptionId
        );
      }

      await api.setDiscountConfig(
        data._id,
        !isBespokePaymentsOnly && hasDiscount ? Number(discountPercent) : null,
        !isBespokePaymentsOnly && hasDiscount ? discountDuration : null,
        !isBespokePaymentsOnly &&
          hasDiscount &&
          discountDuration === "repeating"
          ? Number(discountMonths)
          : null,
        subscriptionId
      );

      const currentSubscriptionConfirmed = data.subscriptionConfirmed ?? false;
      if (
        isUkPaymentsOnly &&
        paymentsOnlyPricingVisible !== currentSubscriptionConfirmed
      ) {
        await api.toggleSubscriptionConfirmed(data._id);
      }

      showNotification(
        "Subscription settings updated",
        NotificationTypes.SUCCESS
      );
      onSuccess();
      onClose();
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Failed to update subscription settings"),
        NotificationTypes.WARNING
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const modeLabel =
    SUBSCRIPTION_WIZARD_PRICING_MODE_OPTIONS.find(
      (o) => o.mode === selectedMode
    )?.label ?? selectedMode;

  const usesHighRiskTier = feeSchedule.some((schedule) =>
    schedule.rules.some(
      (rule) =>
        rule.pricingStrategy === SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
    )
  );

  const interimLabel =
    INTERIM_INTERVAL_OPTIONS.find((o) => o.value === interimInterval)?.label ??
    "Monthly only";

  const formatPaymentsAccess = (access: {
    canAccessRecurringPaymentsUk?: boolean;
    canAccessVrpUk?: boolean;
    canAccessBulkPaymentsUk?: boolean;
  }) =>
    [
      `Recurring: ${access.canAccessRecurringPaymentsUk ? "on" : "off"}`,
      `VRP: ${access.canAccessVrpUk ? "on" : "off"}`,
      `Bulk: ${access.canAccessBulkPaymentsUk ? "on" : "off"}`,
    ].join("\n");

  const formatEuPaymentCurrencies = (currencies?: string[] | null) =>
    currencies?.length
      ? currencies.map((currency) => currency.toUpperCase()).join(", ")
      : "None";

  // ── Current vs. New snapshot for review ──────────────────────────────

  const current = isCreateMode
    ? {
        type: "None",
        tier: null,
        billing: null,
        pricingMode: null,
        feesSummary: null,
        interim: "None",
        trial: "None",
        discount: "None",
      }
    : buildCurrentSnapshot(data, activeSub);

  const newSnapshot = {
    type: subscriptionType.replace(/_/g, " "),
    tier: isToolkit ? TIER_LABELS[selectedLevel] ?? selectedLevel : null,
    billing: isToolkit
      ? `${billingInterval === "year" ? "Yearly" : "Monthly"} — ${
          getPlanPrice(subscriptionType, selectedLevel, billingInterval)
            .formatted
        }`
      : null,
    pricingMode: !isToolkit
      ? PRICING_MODE_LABELS[selectedMode] ?? selectedMode
      : null,
    feesSummary:
      !isToolkit && selectedMode === PaymentsPricingMode.RULE_BASED
        ? formatFeeScheduleSummary(feeSchedule)
        : !isToolkit &&
          selectedMode === PaymentsPricingMode.MONTHLY_FEE &&
          monthlyFeeMinor != null
        ? `${formatMoneyFromPence(
            monthlyFeeMinor,
            paymentsBillingCurrency
          )}/month`
        : null,
    interim: !isToolkit && interimSupported ? interimLabel : "None",
    trial: canConfigureTrial
      ? trialMode === "custom"
        ? `Custom (${trialDays || "0"}d)`
        : trialMode === "none"
        ? "No trial"
        : `Default (${getDefaultTrialDays(
            subscriptionType,
            usesHighRiskTier
          )}-day)`
      : current.trial,
    discount: hasDiscount
      ? `${discountPercent || "0"}% off — ${
          discountDuration === "repeating"
            ? `${discountMonths || "0"} months`
            : discountDuration
        }`
      : "None",
  };

  type DiffItem = { label: string; old: string; new: string };

  const diffItems: DiffItem[] = [
    { label: "Type", old: current.type, new: newSnapshot.type },
    ...(isCreateMode
      ? [
          {
            label: "Initial status",
            old: "—",
            new: initialStatus,
          },
          {
            label: "Currency",
            old: "—",
            new: subscriptionCurrency.toUpperCase(),
          },
        ]
      : []),
    ...(isToolkit || current.tier
      ? [
          {
            label: "Tier",
            old: current.tier ?? "—",
            new: newSnapshot.tier ?? "—",
          },
        ]
      : []),
    ...(isToolkit || current.billing
      ? [
          {
            label: "Billing",
            old: current.billing ?? "—",
            new: newSnapshot.billing ?? "—",
          },
        ]
      : []),
    ...(!isToolkit || current.pricingMode
      ? [
          {
            label: "Pricing mode",
            old: current.pricingMode ?? "—",
            new: newSnapshot.pricingMode ?? "—",
          },
        ]
      : []),
    ...(newSnapshot.feesSummary || current.feesSummary
      ? [
          {
            label: "Fees",
            old: current.feesSummary ?? "—",
            new: newSnapshot.feesSummary ?? "—",
          },
        ]
      : []),
    ...(!isToolkit
      ? [
          {
            label: isEuPaymentsOnly ? "Payment currencies" : "Payment access",
            old: isEuPaymentsOnly
              ? formatEuPaymentCurrencies(activeSub?.enabledPaymentCurrencies)
              : formatPaymentsAccess({
                  canAccessVrpUk: data.canAccessVrpUk === true,
                  canAccessRecurringPaymentsUk:
                    data.canAccessRecurringPaymentsUk === true,
                  canAccessBulkPaymentsUk:
                    data.canAccessBulkPaymentsUk === true,
                }),
            new: isEuPaymentsOnly
              ? formatEuPaymentCurrencies(enabledPaymentCurrencies)
              : formatPaymentsAccess({
                  canAccessRecurringPaymentsUk,
                  canAccessVrpUk,
                  canAccessBulkPaymentsUk,
                }),
          },
        ]
      : []),
    {
      label: "Interim billing",
      old: current.interim,
      new: newSnapshot.interim,
    },
    { label: "Trial", old: current.trial, new: newSnapshot.trial },
    { label: "Discount", old: current.discount, new: newSnapshot.discount },
    ...(needsBillingProvider
      ? [
          {
            label: "Billing provider (company)",
            old: billingProviderLabel(currentCompanyProvider),
            new: billingProviderLabel(selectedProvider),
          },
        ]
      : []),
  ];

  const hasAnyChange = diffItems.some((d) => d.old !== d.new);

  // ── Render ────────────────────────────────────────────────────────────

  const stepBarRef = useRef<HTMLDivElement>(null);

  const scrollToActiveStep = useCallback(() => {
    if (!stepBarRef.current) return;
    const active = stepBarRef.current.querySelector(
      "[data-active='true']"
    ) as HTMLElement;
    if (active) {
      const container = stepBarRef.current;
      const scrollLeft =
        active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToActiveStep();
  }, [stepIdx, scrollToActiveStep]);

  const ukPaymentsValidation = validatePaymentsWizardStep(
    paymentsDraft,
    "uk",
    paymentsBillingCurrency
  );
  const euPaymentsValidation = validatePaymentsWizardStep(
    paymentsDraft,
    "eu",
    paymentsBillingCurrency
  );

  const renderStepBar = () => (
    <StepBarOuter>
      <StepBarScroll ref={stepBarRef}>
        {flow.map((step, i) => {
          const isActive = stepIdx === i;
          const isDone = stepIdx > i;
          return (
            <span key={step} style={{ display: "contents" }}>
              <StepItem
                $active={isActive}
                $done={isDone}
                data-active={isActive}
              >
                <StepDot $active={isActive} $done={isDone}>
                  {isDone ? "✓" : i + 1}
                </StepDot>
                {STEP_LABELS[step]}
              </StepItem>
              {i < flow.length - 1 && <StepConnector $done={isDone} />}
            </span>
          );
        })}
      </StepBarScroll>
    </StepBarOuter>
  );

  return (
    <Modal onHide={onClose}>
      <WizardContainer>
        <WizardHeader>
          <Typography variant={TypographyVariant.HEADER3}>
            Customise Subscription
          </Typography>
          <CloseButton onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </WizardHeader>

        {renderStepBar()}

        {/* ── Current state banner (always visible) ──────────────────── */}
        {hasActiveSubscription && currentStep !== "review" && (
          <CurrentStateBanner>
            <CurrentStateTitle>Current configuration</CurrentStateTitle>
            <CurrentStateGrid>
              <CurrentStateChip>
                <ChipLabel>Type:</ChipLabel>
                <ChipValue>{current.type}</ChipValue>
              </CurrentStateChip>
              {current.tier && (
                <CurrentStateChip>
                  <ChipLabel>Tier:</ChipLabel>
                  <ChipValue>{current.tier}</ChipValue>
                </CurrentStateChip>
              )}
              {current.pricingMode && (
                <CurrentStateChip>
                  <ChipLabel>Pricing:</ChipLabel>
                  <ChipValue>{current.pricingMode}</ChipValue>
                </CurrentStateChip>
              )}
              {current.feesSummary && current.feesSummary !== "—" && (
                <CurrentStateChip>
                  <ChipLabel>Fees:</ChipLabel>
                  <ChipValue>{current.feesSummary}</ChipValue>
                </CurrentStateChip>
              )}
              <CurrentStateChip>
                <ChipLabel>Interim:</ChipLabel>
                <ChipValue>{current.interim}</ChipValue>
              </CurrentStateChip>
              <CurrentStateChip>
                <ChipLabel>Trial:</ChipLabel>
                <ChipValue>{current.trial}</ChipValue>
              </CurrentStateChip>
              {current.discount !== "None" && (
                <CurrentStateChip>
                  <ChipLabel>Discount:</ChipLabel>
                  <ChipValue>{current.discount}</ChipValue>
                </CurrentStateChip>
              )}
            </CurrentStateGrid>
          </CurrentStateBanner>
        )}

        {/* ── Step: Subscription Type ──────────────────────────────────── */}
        {currentStep === "type" && !lockedType && (
          <>
            {hasActiveSubscription && (
              <InfoBanner>
                This company has an active subscription. Changing the
                subscription type will cancel the current one and create a new
                subscription in Stripe. Tier changes within the same type are
                prorated automatically.
              </InfoBanner>
            )}
            <OptionGrid>
              <OptionCard
                $selected={
                  subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY
                }
                onClick={() =>
                  handleTypeChange(SubscriptionOptionEnum.PAYMENTS_ONLY)
                }
              >
                <OptionCardTitle>Payments Only</OptionCardTitle>
                <OptionCardDesc>
                  Usage-based metered billing for payment processing. Configure
                  custom flat fees, percentage fees, and interim billing.
                </OptionCardDesc>
              </OptionCard>
              <OptionCard
                $selected={
                  subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY_EU
                }
                onClick={() =>
                  handleTypeChange(SubscriptionOptionEnum.PAYMENTS_ONLY_EU)
                }
              >
                <OptionCardTitle>EU Open Banking Payments</OptionCardTitle>
                <OptionCardDesc>
                  Independent international Open Banking subscription with
                  explicit payment currencies and EU Single Payment pricing.
                </OptionCardDesc>
              </OptionCard>
              <OptionCard
                $selected={subscriptionType === SubscriptionOptionEnum.TOOLKIT}
                onClick={() => handleTypeChange(SubscriptionOptionEnum.TOOLKIT)}
              >
                <OptionCardTitle>Toolkit</OptionCardTitle>
                <OptionCardDesc>
                  Full e-commerce toolkit with tiered plans (Basic →
                  Enterprise). Includes products, integrations, users, and
                  payment limits.
                </OptionCardDesc>
              </OptionCard>
              <OptionCard
                $selected={
                  subscriptionType === SubscriptionOptionEnum.TOOLKIT_MOBILE
                }
                onClick={() =>
                  handleTypeChange(SubscriptionOptionEnum.TOOLKIT_MOBILE)
                }
              >
                <OptionCardTitle>Toolkit Mobile</OptionCardTitle>
                <OptionCardDesc>
                  Mobile-focused toolkit variant with same tier structure and
                  limits as Toolkit.
                </OptionCardDesc>
              </OptionCard>
            </OptionGrid>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={onClose}>
                Cancel
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Tier & Billing (TOOLKIT) ──────────────────────────── */}
        {currentStep === "tier" && (
          <>
            <ConfigSection>
              <ConfigRow>
                <ConfigLabel>Subscription currency</ConfigLabel>
                <select
                  value={subscriptionCurrency}
                  disabled={!isCreateMode}
                  onChange={(event) =>
                    setSubscriptionCurrency(event.target.value)
                  }
                >
                  <option value="gbp">GBP</option>
                  <option value="eur">EUR</option>
                  <option value="usd">USD</option>
                </select>
              </ConfigRow>
            </ConfigSection>

            <IntervalToggle>
              <IntervalBtn
                $active={billingInterval === "month"}
                onClick={() => setBillingInterval("month")}
              >
                Monthly
              </IntervalBtn>
              <IntervalBtn
                $active={billingInterval === "year"}
                onClick={() => setBillingInterval("year")}
              >
                Yearly
              </IntervalBtn>
            </IntervalToggle>

            <TierGrid>
              {TIERS.map((tier) => {
                const { formatted } = getPlanPrice(
                  subscriptionType,
                  tier,
                  billingInterval
                );
                const limits = getPlanLimits(subscriptionType, tier);
                return (
                  <TierCard
                    key={tier}
                    $selected={selectedLevel === tier}
                    onClick={() => setSelectedLevel(tier)}
                  >
                    <TierName>{TIER_LABELS[tier]}</TierName>
                    <TierPrice>{formatted}</TierPrice>
                    {limits && <TierLimit>{limits}</TierLimit>}
                  </TierCard>
                );
              })}
            </TierGrid>

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Pricing Mode (PAYMENTS_ONLY) ──────────────────────── */}
        {currentStep === "pricing_mode" && (
          <>
            <ConfigSection>
              <ConfigRow>
                <ConfigLabel>Subscription currency</ConfigLabel>
                <select
                  value={subscriptionCurrency}
                  disabled={!isCreateMode}
                  onChange={(event) =>
                    handleSubscriptionCurrencyChange(event.target.value)
                  }
                >
                  <option value="gbp">GBP</option>
                  <option value="eur">EUR</option>
                  <option value="usd">USD</option>
                  <option value="">Let the user decide</option>
                </select>
              </ConfigRow>
            </ConfigSection>

            <OptionGrid>
              {SUBSCRIPTION_WIZARD_PRICING_MODE_OPTIONS.map(
                ({ mode, label, description }) => (
                  <OptionCard
                    key={mode}
                    $selected={selectedMode === mode}
                    onClick={() => handlePaymentsPricingModeChange(mode)}
                  >
                    <OptionCardTitle>{label}</OptionCardTitle>
                    <OptionCardDesc>{description}</OptionCardDesc>
                  </OptionCard>
                )
              )}
            </OptionGrid>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Configure UK Payments (PAYMENTS_ONLY) ─────────────── */}
        {currentStep === "configure_uk" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              <strong>Mode:</strong> {modeLabel}
            </Typography>

            <ConfigSection>
              {selectedMode === PaymentsPricingMode.BESPOKE && (
                <InfoBanner>Bespoke pricing — no automated billing.</InfoBanner>
              )}

              {selectedMode === PaymentsPricingMode.MONTHLY_FEE && (
                <>
                  <InfoBanner>
                    One fixed monthly Payments Only charge in the subscription
                    currency. Payment access is configured separately.
                  </InfoBanner>
                  <ConfigRow>
                    <ConfigLabel>Monthly fee (£)</ConfigLabel>
                    <TextInput
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={monthlyFeeInput}
                      onWheel={preventNumberInputWheel}
                      onChange={(event) => {
                        const value = sanitizeMoneyInput(event.target.value);
                        setMonthlyFeeInput(value);
                        handleMonthlyFeeChange(moneyInputToMinor(value));
                      }}
                    />
                  </ConfigRow>
                </>
              )}

              {isConfigurablePaymentsWizardMode(selectedMode) && (
                <TransactionFeesScheduleEditor
                  value={feeSchedule}
                  onChange={setFeeSchedule}
                  pricingMode={selectedMode}
                  scope="uk"
                  canAccessInternationalOb={canAccessInternationalOb}
                  canAccessRecurringPaymentsUk={canAccessRecurringPaymentsUk}
                  canAccessVrpUk={canAccessVrpUk}
                  canAccessBulkPaymentsUk={canAccessBulkPaymentsUk}
                  enabledPaymentCurrencies={enabledPaymentCurrencies}
                  onEnabledPaymentCurrenciesChange={
                    handleEnabledPaymentCurrenciesChange
                  }
                  billingCurrency={paymentsBillingCurrency}
                  onCapabilitiesChange={handlePaymentsCapabilitiesChange}
                />
              )}
            </ConfigSection>

            {!ukPaymentsValidation.valid && ukPaymentsValidation.message && (
              <InfoBanner $variant="warning">
                {ukPaymentsValidation.message}
              </InfoBanner>
            )}

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={!ukPaymentsValidation.valid}
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Configure EU Open Banking Payments ───────────────── */}
        {currentStep === "configure_eu" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              <strong>Mode:</strong> {modeLabel}
            </Typography>

            <ConfigSection>
              {selectedMode === PaymentsPricingMode.BESPOKE && (
                <InfoBanner>Bespoke pricing — no automated billing.</InfoBanner>
              )}

              {selectedMode === PaymentsPricingMode.MONTHLY_FEE && (
                <>
                  <InfoBanner>
                    One fixed monthly charge in the subscription currency.
                    Enabled payment currencies define subscription access.
                  </InfoBanner>
                  <ConfigRow>
                    <ConfigLabel>
                      Monthly fee ({subscriptionCurrency.toUpperCase()})
                    </ConfigLabel>
                    <TextInput
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={monthlyFeeInput}
                      onWheel={preventNumberInputWheel}
                      onChange={(event) => {
                        const value = sanitizeMoneyInput(event.target.value);
                        setMonthlyFeeInput(value);
                        handleMonthlyFeeChange(moneyInputToMinor(value));
                      }}
                    />
                  </ConfigRow>
                </>
              )}

              {isConfigurablePaymentsWizardMode(selectedMode) && (
                <TransactionFeesScheduleEditor
                  value={feeSchedule}
                  onChange={setFeeSchedule}
                  pricingMode={selectedMode}
                  scope="eu"
                  standaloneEu
                  canAccessInternationalOb
                  canAccessRecurringPaymentsUk={false}
                  canAccessVrpUk={false}
                  canAccessBulkPaymentsUk={false}
                  enabledPaymentCurrencies={enabledPaymentCurrencies}
                  onEnabledPaymentCurrenciesChange={
                    handleEnabledPaymentCurrenciesChange
                  }
                  billingCurrency={paymentsBillingCurrency}
                  fxRates={pricingFxRates}
                  fxRatesLoading={pricingFxRatesLoading}
                  fxRatesError={pricingFxRatesError}
                />
              )}
            </ConfigSection>

            {!euPaymentsValidation.valid && euPaymentsValidation.message && (
              <InfoBanner $variant="warning">
                {euPaymentsValidation.message}
              </InfoBanner>
            )}

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={!euPaymentsValidation.valid}
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Interim Billing (PAYMENTS_ONLY) ───────────────────── */}
        {currentStep === "interim" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              <strong>Interim billing frequency</strong>
            </Typography>

            {!interimSupported ? (
              <InfoBanner>
                {selectedMode === PaymentsPricingMode.BESPOKE
                  ? "Not applicable for Bespoke pricing."
                  : "Interim charges are available only when every configured usage fee uses Custom fees."}
              </InfoBanner>
            ) : (
              <OptionGrid>
                {INTERIM_INTERVAL_OPTIONS.map(
                  ({ value, label, description }) => (
                    <OptionCard
                      key={String(value)}
                      $selected={interimInterval === value}
                      onClick={() => setInterimInterval(value)}
                    >
                      <OptionCardTitle>{label}</OptionCardTitle>
                      <OptionCardDesc>{description}</OptionCardDesc>
                    </OptionCard>
                  )
                )}
              </OptionGrid>
            )}

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Status & Discount ─────────────────────────────────── */}
        {currentStep === "trial_discount" && (
          <>
            <ConfigSection>
              {isCreateMode && (
                <>
                  <OptionLabel>Initial status</OptionLabel>
                  <OptionsRow>
                    <OptionCard
                      $selected={initialStatus === "incomplete"}
                      onClick={() => setInitialStatus("incomplete")}
                      style={{ flex: 1 }}
                    >
                      <OptionCardTitle>Incomplete</OptionCardTitle>
                      <OptionCardDesc>
                        Preconfigure the subscription. The customer activates it
                        later.
                      </OptionCardDesc>
                    </OptionCard>
                    <OptionCard
                      $selected={initialStatus === "trialing"}
                      $disabled={isBespokePaymentsOnly || isCurrencyDeferred}
                      onClick={() => {
                        if (!isBespokePaymentsOnly && !isCurrencyDeferred) {
                          setInitialStatus("trialing");
                        }
                      }}
                      style={{ flex: 1 }}
                    >
                      <OptionCardTitle>Trialing</OptionCardTitle>
                      <OptionCardDesc>
                        Start the subscription and trial immediately.
                      </OptionCardDesc>
                    </OptionCard>
                    <OptionCard
                      $selected={initialStatus === "active"}
                      $disabled={isCurrencyDeferred}
                      onClick={() => {
                        if (!isCurrencyDeferred) setInitialStatus("active");
                      }}
                      style={{ flex: 1 }}
                    >
                      <OptionCardTitle>Active</OptionCardTitle>
                      <OptionCardDesc>
                        Start the subscription immediately without trial.
                      </OptionCardDesc>
                    </OptionCard>
                  </OptionsRow>

                  {isBespokePaymentsOnly && (
                    <InfoBanner>
                      Trialing is not available for Bespoke pricing.
                    </InfoBanner>
                  )}
                  {isCurrencyDeferred && (
                    <InfoBanner>
                      Only Incomplete is available when the billing currency is
                      left for the customer to decide.
                    </InfoBanner>
                  )}

                  {preconfigurePreview?.warning && (
                    <InfoBanner
                      $variant={
                        preconfigurePreview.providerReady === false
                          ? "warning"
                          : "info"
                      }
                    >
                      {preconfigurePreview.warning}
                      {preconfigurePreview.amountMinor > 0 && (
                        <>
                          {" "}
                          Upfront amount:{" "}
                          <strong>
                            {preconfigurePreview.currency?.toUpperCase()}{" "}
                            {(preconfigurePreview.amountMinor / 100).toFixed(2)}
                          </strong>
                          .
                        </>
                      )}
                    </InfoBanner>
                  )}

                  <div style={{ marginTop: 12 }} />
                </>
              )}

              <OptionLabel>Trial period</OptionLabel>
              {!canConfigureTrial ? (
                <InfoBanner>
                  {isBespokePaymentsOnly
                    ? "Trial configuration is not available for Bespoke pricing."
                    : initialStatus === "active"
                    ? "Trial configuration is not available when the subscription starts as active."
                    : "Trial configuration is only available for subscriptions that are currently trialing."}
                </InfoBanner>
              ) : (
                <>
                  {isTrialing && (
                    <InfoBanner>
                      Currently on trial. Changes update the trial end date
                      immediately.
                    </InfoBanner>
                  )}
                  <OptionsRow>
                    <OptionCard
                      $selected={trialMode === "default"}
                      onClick={() => setTrialMode("default")}
                      style={{ flex: 1 }}
                    >
                      <OptionCardTitle>
                        {`Default (${getDefaultTrialDays(
                          subscriptionType,
                          usesHighRiskTier
                        )}-day)`}
                      </OptionCardTitle>
                    </OptionCard>
                    <OptionCard
                      $selected={trialMode === "custom"}
                      onClick={() => setTrialMode("custom")}
                      style={{ flex: 1 }}
                    >
                      <OptionCardTitle>Custom</OptionCardTitle>
                      {trialMode === "custom" && (
                        <InlineInput
                          type="number"
                          placeholder="Days"
                          value={trialDays}
                          onWheel={preventNumberInputWheel}
                          onChange={(e) => setTrialDays(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </OptionCard>
                    <OptionCard
                      $selected={trialMode === "none"}
                      onClick={() => {
                        if (initialStatus !== "trialing") setTrialMode("none");
                      }}
                      style={{ flex: 1 }}
                      $disabled={initialStatus === "trialing"}
                    >
                      <OptionCardTitle>No trial</OptionCardTitle>
                    </OptionCard>
                  </OptionsRow>
                </>
              )}

              <div style={{ marginTop: 12 }} />

              <OptionLabel>Discount coupon</OptionLabel>
              {isBespokePaymentsOnly ? (
                <InfoBanner>
                  Discount coupons are not available for Bespoke pricing.
                </InfoBanner>
              ) : (
                <>
                  {hasActiveSubscription && hasDiscount && (
                    <InfoBanner>
                      The discount will be stored and applied on upcoming
                      invoices.
                    </InfoBanner>
                  )}
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={hasDiscount}
                      onChange={(e) => setHasDiscount(e.target.checked)}
                    />
                    Apply a discount coupon
                  </CheckboxRow>

                  {hasDiscount && (
                    <>
                      <ConfigRow>
                        <ConfigLabel>Discount percentage (%)</ConfigLabel>
                        <InlineInput
                          type="number"
                          placeholder="e.g. 20"
                          value={discountPercent}
                          onWheel={preventNumberInputWheel}
                          onChange={(e) => setDiscountPercent(e.target.value)}
                          style={{ width: 120 }}
                        />
                      </ConfigRow>

                      <OptionLabel>Duration</OptionLabel>
                      <OptionsRow>
                        <OptionCard
                          $selected={discountDuration === "once"}
                          onClick={() => setDiscountDuration("once")}
                          style={{ flex: 1 }}
                        >
                          <OptionCardTitle>Once</OptionCardTitle>
                        </OptionCard>
                        <OptionCard
                          $selected={discountDuration === "forever"}
                          onClick={() => setDiscountDuration("forever")}
                          style={{ flex: 1 }}
                        >
                          <OptionCardTitle>Forever</OptionCardTitle>
                        </OptionCard>
                        <OptionCard
                          $selected={discountDuration === "repeating"}
                          onClick={() => setDiscountDuration("repeating")}
                          style={{ flex: 1 }}
                        >
                          <OptionCardTitle>Repeating</OptionCardTitle>
                          {discountDuration === "repeating" && (
                            <InlineInput
                              type="number"
                              placeholder="Months"
                              value={discountMonths}
                              onWheel={preventNumberInputWheel}
                              onChange={(e) =>
                                setDiscountMonths(e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </OptionCard>
                      </OptionsRow>
                    </>
                  )}
                </>
              )}
            </ConfigSection>

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={!statusStepValid}
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Billing provider ──────────────────────────────────── */}
        {currentStep === "billing_provider" && (
          <>
            <BillingProviderStep
              currentProvider={currentCompanyProvider}
              value={selectedProvider}
              onChange={setSelectedProvider}
              disabled={!canManageCompanies || isSaving}
            />
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={!selectedProvider}
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Review ────────────────────────────────────────────── */}
        {currentStep === "review" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              {hasAnyChange
                ? "Review changes before saving."
                : "No changes detected — configuration matches current state."}
            </Typography>

            <DiffTable>
              <DiffHeader>
                <div>Setting</div>
                <div>Current</div>
                <div />
                <div>New</div>
              </DiffHeader>
              {diffItems.map((item) => {
                const changed = item.old !== item.new;
                return (
                  <DiffRow key={item.label} $changed={changed}>
                    <DiffLabel>{item.label}</DiffLabel>
                    <DiffOld $changed={changed}>{item.old}</DiffOld>
                    <DiffArrow>{changed ? "→" : ""}</DiffArrow>
                    <DiffNew $changed={changed}>
                      {changed ? item.new : item.old}
                    </DiffNew>
                  </DiffRow>
                );
              })}
            </DiffTable>

            <InfoBanner $variant={hasAnyChange ? "warning" : "info"}>
              {!hasAnyChange ? (
                <>No changes to apply.</>
              ) : isCreateMode ? (
                initialStatus === "incomplete" ? (
                  <>
                    This configuration will be saved as an incomplete
                    subscription. The customer or an admin can activate it
                    later.
                  </>
                ) : (
                  <>
                    This subscription will be created immediately with status{" "}
                    <strong>{initialStatus}</strong>.
                  </>
                )
              ) : hasActiveSubscription ? (
                <>
                  <strong>Active subscription detected.</strong> Changes will be
                  applied to Stripe immediately — subscription type/tier
                  switches, discount coupons, trial adjustments, and pricing
                  item swaps all take effect now.
                </>
              ) : (
                <>
                  No active subscription. All settings are stored and will apply
                  when the customer creates their subscription.
                </>
              )}
            </InfoBanner>

            {subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY && (
              <PricingVisibleRow>
                <CheckboxRow>
                  <input
                    type="checkbox"
                    checked={paymentsOnlyPricingVisible}
                    onChange={(e) =>
                      setPaymentsOnlyPricingVisible(e.target.checked)
                    }
                    disabled={!canManageCompanies || isSaving}
                  />
                  Subscription pricing confirmed
                </CheckboxRow>
                <TooltipText
                  id="subscription-wizard-pricing-visible-tooltip"
                  text="Enable this once the subscription pricing for Payments sub has been finalized between sales and the client. Enabling this will display the actual subscription price on the user's billing page and clear the unconfirmed price warnings."
                >
                  <span style={{ cursor: "help" }}>ⓘ</span>
                </TooltipText>
              </PricingVisibleRow>
            )}

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={isSaving}
                onClick={handleSave}
              >
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </WizardActions>
          </>
        )}
      </WizardContainer>
    </Modal>
  );
};

export default SubscriptionWizardModal;
